import { EventTypes, Node as CraftNode, useEditor } from '@candulabs/craft';
import React, { MutableRefObject, useEffect, useRef } from 'react';

import { getNodeId, HOTSPOT } from '@candu-ai/styleguide/selectors';
import { toCssSelector } from '@candu-ai/stylesheet/utils';
import { useEventing } from 'commons/eventing';
import { useOutsideClick } from 'commons/hooks';
import Placeholder from 'craft/components/Placeholder';
import { createVariantsSelector } from 'craft/components/SelectedToolbar';
import { useActiveTooltip } from 'craft/components/Tooltip';
import { VariantsSelectorToolbox } from 'craft/components/VariantsSelectorToolbox';
import { BuiltInComponents } from 'craft/constants';
import { VIEWPORT_CONTENT_ID } from 'craft/containers/Viewport';
import { useNodeContext, useNodeCustom, useSelection, useTooltipPlacement } from 'craft/hooks';
import { useMultiCssSelector } from 'craft/hooks/useMultiCssSelector';
import { HotspotGroupSettings } from 'craft/resolvers/Hotspot/toolbox/HotspotGroupSettings';
import { usePlacementMode, useToolboxState, useToolboxTab } from 'craft/state';
import { CssSelectorId, Resolver, ToolboxComponentSections } from 'craft/types';
import { hasDescendants } from 'craft/utils';

import { Beacon } from './Beacon';
import { HotspotTooltip } from './HotspotTooltip';
import { BeaconSettings } from './toolbox/BeaconSettings';
import { HotspotArrowSize } from './toolbox/HotspotArrowSize';
import { HotspotDismiss } from './toolbox/HotspotDismiss';
import { TooltipPosition } from './toolbox/TooltipPosition';
import { BeaconPosition, TooltipPosition as Position } from './types';
import { useBeaconPosition } from './utils/useBeaconPosition';
import { useTooltipPosition } from './utils/useTooltipPosition';

const FREEZE_SELECTORS = {};
Object.freeze(FREEZE_SELECTORS);

export const Hotspot: Resolver = ({ children, variant = 'base' }: any) => {
  const { selection } = useSelection();
  const { actions } = useEditor();
  const { id } = useNodeContext();
  const { placement: hotspotPlacement } = useTooltipPlacement(id);
  const activeHotspot = useActiveTooltip();
  const { previewSelector } = usePlacementMode();
  const { forcePlacementsTab } = useToolboxTab();

  const tooltipPosition = useNodeCustom(id)?.custom?.tooltipPosition || Position.Auto;
  const beaconRelativePosition = useNodeCustom(id)?.custom?.beaconPosition || BeaconPosition.Left;
  const customCssSelectors = useNodeCustom(id)?.cssSelectors || FREEZE_SELECTORS;
  const { css: beaconCss } = useMultiCssSelector([id], CssSelectorId.HotspotBeacon);
  const { css: wrapperCss } = useMultiCssSelector([id], CssSelectorId.HotspotWrapper);
  const { css: bodyCss } = useMultiCssSelector([id], CssSelectorId.HotspotBody);

  const { track } = useEventing();
  const { minimizeToolboxOnSnapshotClick } = useToolboxState();

  const wrapperClassName = toCssSelector(HOTSPOT.Wrapper, {
    output: 'html',
    variant: variant ? { variant } : undefined,
  });

  const bodyClassName = toCssSelector(HOTSPOT.Body, {
    output: 'html',
    variant: variant ? { variant } : undefined,
  });

  const arrowClassName = toCssSelector(HOTSPOT.Arrow, {
    output: 'html',
    variant: variant ? { variant } : undefined,
  });

  const isBeaconActive = activeHotspot?.id === id;
  const hotspotSelector =
    forcePlacementsTab && isBeaconActive ? previewSelector : hotspotPlacement?.selector;

  const beaconRef = useRef<HTMLDivElement | undefined>();

  const { refs, css, recalculate } = useTooltipPosition({
    beaconRef,
    tooltipPosition,
    isActive: isBeaconActive,
    selector: hotspotSelector,
  });

  const { beaconPosition, isBeaconVisible } = useBeaconPosition({
    placementSelector: hotspotSelector,
    refs: { ...refs, beacon: beaconRef },
    recalculateTooltipPosition: recalculate,
    selectedNodes: selection.all,
    nodeId: id,
    isBeaconActive,
    positionRelativeTo: beaconRelativePosition,
  });

  useEffect(() => {
    recalculate();
  }, [
    activeHotspot?.id,
    refs.wrapper.current,
    hotspotPlacement,
    tooltipPosition,
    beaconCss,
    wrapperCss,
    bodyCss,
    customCssSelectors,
  ]);

  // @ts-ignore
  useOutsideClick(refs.wrapper?.current, (e) => {
    // @ts-ignore
    const targetId = e?.target?.id;

    if (targetId !== VIEWPORT_CONTENT_ID && targetId !== 'hotspotGroupBackdrop') {
      // only consider an outside click when it's triggered from inside the canvas
      return;
    }

    if (!refs.wrapper?.current?.id.includes(activeHotspot?.id || '')) {
      // if the outside click handler fired is tied to a different hotspot, ignore it
      return;
    }

    if (minimizeToolboxOnSnapshotClick()) {
      track('editor.dockedToolbox.snapshotClose');
    }

    // toggle Hotspot focus on click outside
    if (activeHotspot?.id && !selection.first) {
      actions.setEventNodes(EventTypes.selected, [activeHotspot.id]);
    } else {
      actions.setEventNodes(EventTypes.selected, []);
    }
  });

  if (!hotspotPlacement || !isBeaconVisible) {
    return null;
  }

  // We need ref updates to have side effects, so we wrap the base ref with some extra logic
  const refHandler = (onRef: MutableRefObject<HTMLDivElement | undefined>) => (
    ref: HTMLDivElement,
  ) => {
    // extra paranoid to avoid recalculating if ref hasn't actually changed
    if (!ref || onRef.current === ref) {
      return;
    }
    // eslint-disable-next-line no-param-reassign
    onRef.current = ref;
    recalculate();
  };

  const hasChildren = hasDescendants(children);

  return (
    <div key={`hotspot-node-${id}`} id={getNodeId(id)}>
      <div
        className={HOTSPOT.BeaconWrapper}
        style={{ position: 'absolute', zIndex: 2, ...beaconPosition }}
      >
        <Beacon
          key={`hotspot-beacon-${id}`}
          variant={variant}
          nodeId={id}
          beaconRef={refHandler(beaconRef)}
        />
      </div>
      {isBeaconActive && (
        <HotspotTooltip
          key={`hotspot-tooltip-${id}`}
          nodeId={id}
          isDismissible
          wrapperRef={refHandler(refs.wrapper)}
          wrapperCss={css.wrapper}
          arrowRef={refHandler(refs.arrow)}
          arrowCss={css.arrow}
          bodyCss={bodyCss}
          classNameObj={{
            Arrow: arrowClassName,
            Body: bodyClassName,
            Container: HOTSPOT.Container,
            Wrapper: wrapperClassName,
          }}
        >
          {hasChildren ? children : <Placeholder />}
        </HotspotTooltip>
      )}
    </div>
  );
};

Hotspot.craft = {
  name: BuiltInComponents.Hotspot,
  displayName: 'Hotspot',
  isCanvas: true,
  rules: {
    canDelete: (node: CraftNode, helpers) => {
      const isLastRemaining = helpers(node.id).getParent().descendants().length === 1;
      return !isLastRemaining;
    },
    canDrop: (target: CraftNode) => {
      return target.data.name === BuiltInComponents.HotspotGroup;
    },
    canDrag: () => false,
  },
};

Hotspot.toolbox = {
  schema: {
    [ToolboxComponentSections.NameAndType]: {
      variantSelector: VariantsSelectorToolbox,
    },
    [ToolboxComponentSections.Layout]: {
      cssSelectorId: CssSelectorId.HotspotBody,
      blockedDisplays: ['none'],
    },
    [ToolboxComponentSections.Size]: {
      cssSelectorId: CssSelectorId.HotspotBody,
      hasOverflow: true,
      hasPercentageSize: false,
    },
    [ToolboxComponentSections.Spacing]: {
      cssSelectorId: CssSelectorId.HotspotBody,
      hasMargin: false,
    },
    [ToolboxComponentSections.Background]: {
      cssSelectorId: CssSelectorId.HotspotWrapper,
      hasBackgroundImage: false,
    },
    [ToolboxComponentSections.Border]: {
      cssSelectorId: CssSelectorId.HotspotWrapper,
    },
    [ToolboxComponentSections.Shadow]: {
      cssSelectorId: CssSelectorId.HotspotWrapper,
      isFilter: true,
    },
  },
  component: [
    HotspotGroupSettings,
    TooltipPosition,
    HotspotArrowSize,
    BeaconSettings,
    HotspotDismiss,
  ],
};

Hotspot.selectedToolbar = {
  customItems: [
    createVariantsSelector({
      componentName: BuiltInComponents.Hotspot,
      getConditions: (props) =>
        (props.variant?.value
          ? { name: props.variant?.value, variant: props.variant?.value }
          : { name: 'base' }) as Record<string, string>,
      variantToProps: (variant) => ({
        variant: variant.conditions.variant,
      }),
    }),
  ],
};
